import { BillingInsights } from '@features/Admin/Billing/ui/BillingInsights';
import { BillingAlerts } from '@features/Admin/Billing/ui/BillingNotifications';
import { BillingUsage } from '@features/Admin/Billing/ui/BillingUsage';
import { planChangeExplanation } from '@features/Admin/Billing/model/planChangeExplanation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { BillingAutoTopUp } from '@features/Admin/Billing/ui/BillingAutoTopUp';
import { BillingTopUps } from '@features/Admin/Billing/ui/BillingTopUps';
import { BillingPlanOverview } from '@features/Admin/Billing/ui/BillingPlanOverview';
import { BillingPlanOffers } from '@features/Admin/Billing/ui/BillingPlanOffers';
import { BillingSummary } from '@features/Admin/Billing/ui/BillingSummary';
import { BillingCheckoutReview } from '@features/Admin/Billing/ui/BillingCheckoutReview';
import { BillingUpgradeDetails } from '@features/Admin/Billing/ui/BillingUpgradeDetails';
import { billingErrorMessage, hostedBillingUrl } from '@features/Admin/Billing/model/billingError';
import { billingDate } from '@features/Admin/Billing/model/formatBillingUnits';
import PageContainer from '@shared/components/container/PageContainer';
import { useAppDispatch, useAppSelector } from '@shared/store';
import { actions, selectors } from '@shared/store/ducks';
import {
  apiService,
  useStripeScheduledChangeControllerRequestMutation,
  type StripeScheduledChangeRequestDto,
  useBillingControllerCompanyQuery,
  useBillingControllerOffersQuery,
  useStripeBillingHistoryControllerReadQuery,
  useStripeBillingControllerStartCheckoutMutation,
  useStripePortalControllerOpenMutation,
  useStripeSynchronizationControllerReadQuery,
  useStripeSynchronizationControllerRequestMutation,
  useStripeUpgradeControllerApplyMutation,
  useStripeUpgradeControllerPreviewMutation,
  type StripeUpgradePreviewDto,
  type StripeSynchronizationDto,
} from '@shared/services/apiService';

const MAX_REFRESHES = 8;
const MAX_SYNC_CHECKS = 24;

export const BillingPage = () => {
  const user = useAppSelector(selectors.auth.getUser);
  return <BillingPageContent key={`${user?.companyId}:${user?.id}:${user?.role}`} />;
};

const BillingPageContent = () => {
  const dispatch = useAppDispatch();
  const [billingRefreshSignal, setBillingRefreshSignal] = useState(0);
  const user = useAppSelector(selectors.auth.getUser);
  const companyId = user?.companyId;
  const argument = companyId ? { companyId } : skipToken;
  const summary = useBillingControllerCompanyQuery(argument, { refetchOnMountOrArgChange: true });
  const catalog = useBillingControllerOffersQuery(argument, { refetchOnMountOrArgChange: true });
  const history = useStripeBillingHistoryControllerReadQuery(argument, {
    refetchOnMountOrArgChange: true,
  });
  const sync = useStripeSynchronizationControllerReadQuery(argument, {
    refetchOnMountOrArgChange: true,
  });
  const [checkout, checkoutState] = useStripeBillingControllerStartCheckoutMutation();
  const [portal, portalState] = useStripePortalControllerOpenMutation();
  const [requestSync, syncState] = useStripeSynchronizationControllerRequestMutation();
  const [previewUpgrade, previewState] = useStripeUpgradeControllerPreviewMutation();
  const [applyUpgrade, applyState] = useStripeUpgradeControllerApplyMutation();
  const [scheduleChange, scheduledState] = useStripeScheduledChangeControllerRequestMutation();
  const [loadPayment, paymentState] = apiService.useLazyStripeUpgradeControllerPaymentQuery();
  const [search] = useSearchParams();
  const returned = search.get('checkout') === 'success';
  const [confirmingPayment, setConfirmingPayment] = useState(returned);
  const [scheduled, setScheduled] = useState<
    (Omit<StripeScheduledChangeRequestDto, 'requestKey'> & { label: string }) | null
  >(null);
  const [remaining, setRemaining] = useState(returned ? MAX_REFRESHES : 0);
  const [message, setMessage] = useState(
    returned
      ? 'Payment is awaiting server confirmation. Credits appear only after the payment and subscription are verified.'
      : '',
  );
  const [error, setError] = useState('');
  const [planChooserOpen, setPlanChooserOpen] = useState(false);
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | null>(null);
  const checkoutPlan = catalog.data?.plans.find((plan) => plan.id === checkoutPlanId) ?? null;
  const [preview, setPreview] = useState<StripeUpgradePreviewDto | null>(null);
  const [expectedPlanId, setExpectedPlanId] = useState<string | null>(null);
  const keys = useRef(new Map<string, string>());
  const pendingId = useRef<string | null>(null);
  const openingSync = useRef(false);
  const mounted = useRef(false);
  const [syncRequest, setSyncRequest] = useState<StripeSynchronizationDto | null>(null);
  const busy =
    checkoutState.isLoading ||
    portalState.isLoading ||
    syncState.isLoading ||
    previewState.isLoading ||
    applyState.isLoading ||
    scheduledState.isLoading ||
    paymentState.isFetching;
  const { refetch: refreshSummary } = summary;
  const { refetch: refreshCatalog } = catalog;
  const { refetch: refreshSync } = sync;
  const { refetch: refreshHistory } = history;
  const refresh = useCallback(() => {
    setBillingRefreshSignal((value) => value + 1);
    if (companyId) {
      void refreshSummary();
      void refreshCatalog();
      void refreshSync();
      void refreshHistory();
    }
  }, [companyId, refreshSummary, refreshCatalog, refreshSync, refreshHistory]);
  const key = (scope: string) => {
    if (!keys.current.has(scope)) keys.current.set(scope, crypto.randomUUID());
    return keys.current.get(scope)!;
  };
  const failed = useCallback(
    (value: unknown) => {
      setError(billingErrorMessage(value));
      refresh();
    },
    [refresh],
  );

  useEffect(() => {
    dispatch(actions.customizer.toggleLayout('full'));
  }, [dispatch]);
  useEffect(() => {
    setPlanChooserOpen(false);
  }, [companyId, user?.id, user?.role]);
  useEffect(() => {
    const id = catalog.data?.pendingChange?.id;
    if (id && id !== pendingId.current) {
      pendingId.current = id;
      setRemaining(MAX_REFRESHES);
    }
  }, [catalog.data?.pendingChange?.id]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (
      openingSync.current ||
      !companyId ||
      catalog.isError ||
      catalog.isFetching ||
      !catalog.currentData?.canSynchronize
    )
      return;
    openingSync.current = true;
    void requestSync({ companyId })
      .unwrap()
      .then((result) => {
        if (!mounted.current) return;
        setSyncRequest(result);
        setRemaining(MAX_SYNC_CHECKS);
      })
      .catch((value) => {
        if (mounted.current) failed(value);
      });
  }, [
    companyId,
    catalog.currentData?.canSynchronize,
    catalog.isError,
    catalog.isFetching,
    requestSync,
    failed,
  ]);
  useEffect(() => {
    if (!remaining || !companyId) return;
    const timer = window.setTimeout(() => {
      if (syncRequest) void refreshSync();
      else refresh();
      setRemaining((n) => Math.max(0, n - 1));
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [remaining, companyId, syncRequest, refresh, refreshSync]);
  useEffect(() => {
    if (!syncRequest) return;
    const current = sync.isError ? undefined : sync.currentData;
    const completed =
      current?.state === 'idle' &&
      current.completedAt &&
      syncRequest.requestedAt &&
      Date.parse(current.completedAt) >= Date.parse(syncRequest.requestedAt);
    if (completed || remaining === 0) {
      setSyncRequest(null);
      setRemaining(0);
      refresh();
    }
  }, [syncRequest, remaining, sync.currentData, sync.isError, refresh]);

  const startCheckout = async (planVersionId: string) => {
    if (!companyId || !summary.data?.canManage || !catalog.data?.canCheckout) return;
    setCheckoutPlanId(null);
    setError('');
    setExpectedPlanId(planVersionId);
    setConfirmingPayment(true);
    try {
      const result = await checkout({
        companyId,
        stripeCheckoutRequestDto: {
          planVersionId,
          requestKey: key(`checkout:${companyId}:${planVersionId}`),
        },
      }).unwrap();
      if (result.state === 'open' && result.url) {
        window.location.assign(hostedBillingUrl(result.url, 'checkout'));
        return;
      }
      if (result.state === 'expired') keys.current.delete(`checkout:${companyId}:${planVersionId}`);
      setMessage(
        result.state === 'expired'
          ? 'The Checkout session expired. You may start a new session.'
          : 'Subscription activation is pending server confirmation.',
      );
      setRemaining(MAX_REFRESHES);
      refresh();
    } catch (value) {
      failed(value);
    }
  };
  const openPortal = async () => {
    if (!companyId) return;
    setError('');
    try {
      const result = await portal({ companyId }).unwrap();
      window.location.assign(hostedBillingUrl(result.url, 'portal'));
    } catch (value) {
      failed(value);
    }
  };
  const reviewUpgrade = async (planVersionId: string) => {
    if (!companyId) return;
    setError('');
    try {
      setPreview(
        await previewUpgrade({
          companyId,
          stripeUpgradePreviewRequestDto: { planVersionId },
        }).unwrap(),
      );
    } catch (value) {
      failed(value);
    }
  };
  const confirmUpgrade = async () => {
    if (!companyId || !preview) return;
    setError('');
    setExpectedPlanId(preview.nextPlanVersionId);
    try {
      const result = await applyUpgrade({
        companyId,
        stripeUpgradeApplyRequestDto: {
          previewId: preview.previewId,
          requestKey: key(`upgrade:${preview.previewId}`),
        },
      }).unwrap();
      setPreview(null);
      if (result.state === 'failed' || result.state === 'review') {
        setMessage('');
        setError(
          result.state === 'failed'
            ? 'The plan change did not complete. Your current plan remains in effect.'
            : 'The plan change needs billing review. Your current quota has not been increased.',
        );
      } else
        setMessage(
          result.state === 'succeeded'
            ? 'The server confirmed the plan change.'
            : 'The plan change is awaiting payment and effective subscription confirmation. Current quotas stay in effect until verified.',
        );
      setRemaining(MAX_REFRESHES);
      refresh();
    } catch (value) {
      failed(value);
    }
  };

  const confirmScheduled = async () => {
    if (!companyId || !scheduled) return;
    setError('');
    const scope = `scheduled:${companyId}:${summary.data?.endsAt}:${scheduled.action}:${scheduled.planVersionId ?? ''}`;
    try {
      const result = await scheduleChange({
        companyId,
        stripeScheduledChangeRequestDto: {
          action: scheduled.action,
          ...(scheduled.planVersionId ? { planVersionId: scheduled.planVersionId } : {}),
          requestKey: key(scope),
        },
      }).unwrap();
      if (result.state === 'succeeded') keys.current.delete(scope);
      setConfirmingPayment(false);
      setMessage(
        result.state === 'succeeded'
          ? scheduled.action === 'release_schedule'
            ? 'The server confirmed removal of the scheduled downgrade. Review a fresh plan change when ready.'
            : `The server confirmed the change for ${billingDate(result.effectiveAt)}. The current paid period stays in effect until then.`
          : 'The requested change is awaiting provider confirmation. Reload this page to check its status before requesting another change.',
      );
      setScheduled(null);
      setRemaining(MAX_REFRESHES);
      refresh();
    } catch (value) {
      failed(value);
    }
  };
  const continuePayment = async () => {
    const intentId = catalog.data?.pendingChange?.id;
    if (!companyId || !intentId) return;
    setError('');
    try {
      const result = await loadPayment({ companyId, intentId }, false).unwrap();
      if (result.state === 'available' && result.url) {
        window.location.assign(hostedBillingUrl(result.url, 'invoice'));
        return;
      }
      setMessage(
        'Payment confirmation or the payment page is not yet available. Reload this page to check again.',
      );
      setRemaining(MAX_REFRESHES);
      refresh();
    } catch (value) {
      failed(value);
    }
  };

  const managed = summary.data?.mode === 'credit_managed';
  const isPaid = summary.data?.status === 'paid';
  const inlinePlans =
    managed && ['trial', 'trial_pending', 'expired'].includes(summary.data?.status ?? '');
  const planOptions =
    summary.data && catalog.data ? (
      <BillingPlanOffers
        summary={summary.data}
        catalog={catalog.data}
        busy={busy}
        onSubscribe={(planId) => {
          setPlanChooserOpen(false);
          setCheckoutPlanId(planId);
        }}
        onUpgrade={(planId) => {
          setPlanChooserOpen(false);
          void reviewUpgrade(planId);
        }}
        onDowngrade={(planVersionId, planName) => {
          setPlanChooserOpen(false);
          setScheduled({
            action: 'downgrade',
            planVersionId,
            label: `Change to ${planName} next period`,
          });
        }}
      />
    ) : null;

  return (
    <PageContainer title="Billing" description="Subscription, credits and payment settings">
      <Stack spacing={3} sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, mx: 'auto', width: '100%' }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Billing
          </Typography>
          <Typography color="text.secondary">
            Your company plan, shared credits and payment settings.
          </Typography>
        </Box>
        {error && (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="info">
            {confirmingPayment &&
            summary.data?.status === 'paid' &&
            !catalog.data?.pendingChange &&
            (!expectedPlanId || summary.data.plan?.id === expectedPlanId)
              ? 'The server confirmed the paid plan shown below.'
              : message}
          </Alert>
        )}
        {summary.isLoading && <CircularProgress aria-label="Loading billing" />}
        {(summary.isError || catalog.isError) && (
          <Alert severity="error" action={<Button onClick={refresh}>Retry</Button>}>
            Billing information could not be loaded.
          </Alert>
        )}
        {managed && companyId && summary.data && (
          <BillingAlerts companyId={companyId} summary={summary.data} />
        )}
        {managed && summary.data && (
          <BillingPlanOverview
            summary={summary.data}
            catalog={catalog.data}
            subscription={history.isError ? null : history.currentData?.subscription}
            inlinePlans={!!inlinePlans}
            busy={busy}
            onViewPlans={() => setPlanChooserOpen(true)}
          />
        )}
        {inlinePlans && !!catalog.data?.plans.length && (
          <Stack component="section" aria-labelledby="inline-company-plans-title" spacing={2}>
            <Box>
              <Typography
                id="inline-company-plans-title"
                variant="h5"
                component="h2"
                fontWeight={600}>
                Company plans
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Choose a monthly plan for your company. Credits and call capacity are shared by your
                team.
              </Typography>
            </Box>
            {planOptions}
          </Stack>
        )}
        {summary.data && (
          <BillingSummary
            summary={summary.data}
            catalog={catalog.data}
            showPlanDetails={!managed}
            renderCredits={
              managed && companyId
                ? (availableUnits) => (
                    <BillingTopUps
                      key={`${companyId}:${user?.id}:${user?.role}`}
                      companyId={companyId}
                      summary={summary.data}
                      availableUnits={availableUnits}
                      overview={
                        <BillingInsights
                          companyId={companyId}
                          summary={summary.data!}
                          refreshSignal={billingRefreshSignal}
                          busy={busy}
                          canManage={!!catalog.data?.canOpenPortal}
                          onManage={() => void openPortal()}
                        />
                      }
                      onRefresh={refresh}
                      refreshSignal={billingRefreshSignal}
                      actions={
                        <BillingAutoTopUp
                          key={`auto:${companyId}:${user?.id}:${user?.role}`}
                          companyId={companyId}
                          onRefresh={refresh}
                          refreshSignal={billingRefreshSignal}
                        />
                      }
                    />
                  )
                : undefined
            }
          />
        )}
        {managed && companyId && summary.data && (
          <BillingUsage
            companyId={companyId}
            summary={summary.data}
            refreshSignal={billingRefreshSignal}
          />
        )}
        {summary.data?.mode === 'credit_managed' && (
          <>
            {(catalog.data?.canOpenPortal ||
              catalog.data?.canChangePlan ||
              catalog.data?.canReleaseSchedule ||
              (summary.data?.canManage && catalog.data?.pendingChange?.kind === 'upgrade')) && (
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                {catalog.data?.canChangePlan && (
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={() => setScheduled({ action: 'cancel', label: 'Cancel renewal' })}>
                    Cancel renewal
                  </Button>
                )}
                {catalog.data?.canReleaseSchedule && (
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={() =>
                      setScheduled({
                        action: 'release_schedule',
                        label: 'Remove scheduled downgrade',
                      })
                    }>
                    Remove scheduled downgrade
                  </Button>
                )}
                {summary.data?.canManage && catalog.data?.pendingChange?.kind === 'upgrade' && (
                  <Button
                    variant="contained"
                    disabled={busy}
                    onClick={() => void continuePayment()}>
                    Continue upgrade payment
                  </Button>
                )}
              </Stack>
            )}
            {remaining > 0 && (
              <Typography variant="caption" color="text.secondary" role="status">
                Updating billing…
              </Typography>
            )}
            {sync.isError && (
              <Alert severity="warning">
                Synchronization status could not be loaded. Reload the page to check it again.
              </Alert>
            )}
            {sync.data?.errorCode && (
              <Alert severity="warning">
                {billingErrorMessage({ data: { reason: sync.data.errorCode } })}
              </Alert>
            )}
            {remaining === 0 &&
              (catalog.data?.pendingChange ||
                ['queued', 'processing'].includes(sync.data?.state ?? '')) && (
                <Alert severity="info">
                  Some billing changes are still being verified. Reload this page to check again, or
                  contact your billing administrator.
                </Alert>
              )}
            {!summary.data.canManage && (
              <Typography color="text.secondary">
                A company member with billing-management permission can change payment settings.
              </Typography>
            )}
          </>
        )}
        <Dialog
          open={planChooserOpen && !inlinePlans && !!catalog.data?.plans.length}
          onClose={busy ? undefined : () => setPlanChooserOpen(false)}
          fullWidth
          maxWidth="lg"
          aria-labelledby="company-plans-dialog-title">
          <DialogTitle id="company-plans-dialog-title">Company plans</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography color="text.secondary">
                Compare monthly plans. Your current credits and access stay in effect until a plan
                change is confirmed.
              </Typography>
              {isPaid && !catalog.data?.canChangePlan && (
                <Alert severity="info">
                  {planChangeExplanation(catalog.data?.changePlanUnavailableReason)}
                </Alert>
              )}
              {planOptions}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setPlanChooserOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <BillingCheckoutReview
          plan={summary.data?.canManage && catalog.data?.canCheckout ? checkoutPlan : null}
          summary={summary.data}
          busy={busy}
          onClose={() => setCheckoutPlanId(null)}
          onConfirm={() => {
            if (checkoutPlan) void startCheckout(checkoutPlan.id);
          }}
        />
        <Dialog
          open={!!scheduled}
          onClose={() => {
            if (!busy) setScheduled(null);
          }}
          fullWidth
          maxWidth="sm">
          <DialogTitle>{scheduled?.label}</DialogTitle>
          <DialogContent>
            <Typography>
              {scheduled?.action === 'release_schedule'
                ? 'Remove the owned downgrade schedule while keeping the current subscription. A new upgrade requires a fresh preview after removal is confirmed.'
                : `This changes renewal at the paid period boundary (${billingDate(summary.data?.endsAt ?? null)}). Current credits, features and call capacity stay in effect until then. Active calls may finish.`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setScheduled(null)}>
              Back
            </Button>
            <Button variant="contained" disabled={busy} onClick={() => void confirmScheduled()}>
              Confirm change
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={!!preview}
          onClose={() => {
            if (!busy) setPreview(null);
          }}
          fullWidth
          maxWidth="sm">
          <DialogTitle>Review plan upgrade</DialogTitle>
          <DialogContent>
            {preview && (
              <BillingUpgradeDetails
                preview={preview}
                summary={summary.data}
                nextPlan={catalog.data?.plans.find((plan) => plan.id === preview.nextPlanVersionId)}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreview(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => void confirmUpgrade()} disabled={busy}>
              Confirm paid upgrade
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </PageContainer>
  );
};
